import React, {useEffect, useState} from 'react';
import type {TableColumnsType} from 'antd';
import {Button, message as messageApi, Table, Tooltip, Tag} from 'antd';
import FilterArea, { DEFAULT_DSP_FILTER_OPTIONS, type DspFilterOptions } from '../../central/dsp/components/FilterArea';
import DspFormDrawer from '../../central/dsp/components/DspFormDrawer';
import {GET_DSPS_BY_HUB_ID, GetDspsByHubIdResponse} from '../../../graphql/queries/getDspsByHubId';
import {useSaveDspWithWarehouses} from '../../central/dsp/graphql/mutations/saveDspWithWarehouses';
import {useWarehouseContext} from '../../../contexts/WarehouseContext';
import {useWarehouseOptions} from '../../../hooks/useWarehouseOptions';
import {showError} from 'utils/exceptionUtils';
import {DspType} from '../../../graphql/types/DspType';
import Loading from "../../../components/Loading";
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { SiteType } from '../../../graphql/types/SiteType';
import { GET_ALL_DSPS, useAllDsps } from '../../../graphql/queries/getAllDsps';
import { hasHeadquartersAccess } from 'utils/auth';
import { GET_WAREHOUSE_LIST_FOR_OPTION_BY_HUB_ID } from '../../../graphql/queries/warehouseQueries';
import { WarehouseOption } from '../../../hooks/useWarehouseOptions';
import TablePaginationBar from '../../../components/TablePaginationBar';

const WmsDspPage: React.FC = () => {
    const {t} = useTranslation();
    const { currentWarehouse } = useWarehouseContext();
    const isHub = currentWarehouse?.siteType === SiteType.HUB;
    const hubId = isHub && currentWarehouse?.id ? Number(currentWarehouse.id) : undefined;
    const isHeadquarters = hasHeadquartersAccess();

    // 根据用户角色选择不同的查询
    const { data: hubDspsData, loading: hubDspsLoading, error: hubDspsError, refetch: hubDspsRefetch } = useQuery<GetDspsByHubIdResponse>(
        GET_DSPS_BY_HUB_ID,
        {
            variables: { hubId: hubId! },
            skip: !hubId || isHeadquarters // 总部用户或非 Hub 时跳过此查询
        }
    );

    const { allDsps: allDspsData, loading: allDspsLoading, error: allDspsError, refetch: allDspsRefetch } = useAllDsps();

    // 统一处理数据
    const allDsps = React.useMemo(() => {
        if (isHeadquarters) {
            return allDspsData;
        }
        if (isHub && hubDspsData?.dspList) {
            return hubDspsData.dspList.map(dsp => ({
                ...dsp,
                warehouseIds: dsp.warehouses.map(warehouse => Number(warehouse.id))
            }));
        }
        return [];
    }, [isHeadquarters, allDspsData, isHub, hubDspsData]);

    const loading = isHeadquarters ? allDspsLoading : hubDspsLoading;
    const error = isHeadquarters ? allDspsError : hubDspsError;

    const refetch = () => {
        if (isHeadquarters) {
            allDspsRefetch();
        } else {
            hubDspsRefetch();
        }
    };

    // 获取仓库选项：HUB 经理只能选择该 Hub 下的 warehouse
    const { warehouseOptions: allWarehouseOptions, loading: allWarehousesLoading } = useWarehouseOptions(SiteType.WAREHOUSE);
    
    // 如果是 Hub，查询该 Hub 下的 warehouse
    const { data: hubWarehousesData, loading: hubWarehousesLoading } = useQuery(
        GET_WAREHOUSE_LIST_FOR_OPTION_BY_HUB_ID,
        {
            variables: { hubId: hubId! },
            skip: !isHub || !hubId || isHeadquarters
        }
    );
    
    // 统一处理仓库选项
    const warehouseOptions: WarehouseOption[] = React.useMemo(() => {
        if (isHeadquarters) {
            return allWarehouseOptions;
        }
        if (isHub && hubWarehousesData?.warehouseList) {
            return hubWarehousesData.warehouseList.map((w: { id: string; name: string }) => ({
                id: w.id,
                name: w.name
            }));
        }
        return allWarehouseOptions;
    }, [isHeadquarters, allWarehouseOptions, isHub, hubWarehousesData]);
    
    const warehousesLoading = isHeadquarters ? allWarehousesLoading : hubWarehousesLoading;

    const [drawerVisible, setDrawerVisible] = useState(false);
    const [selectedDsp, setSelectedDsp] = useState<DspType | null>(null);
    const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('create');
    const [filteredData, setFilteredData] = useState<DspType[]>([]);
    const [filters, setFilters] = useState<DspFilterOptions>(DEFAULT_DSP_FILTER_OPTIONS);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const [saveDspWithWarehouses, {loading: savingDsp}] = useSaveDspWithWarehouses();

    // Initialize filtered data when dspData changes
    useEffect(() => {
        setFilteredData(allDsps.filter(dsp => dsp.status === 1));
        setFilters(DEFAULT_DSP_FILTER_OPTIONS);
        setCurrentPage(1); // 重置到第一页
    }, [allDsps]);

    // 当筛选数据变化时，重置到第一页
    useEffect(() => {
        setCurrentPage(1);
    }, [filteredData.length]);

    // Handle GraphQL errors
    useEffect(() => {
        if (error) {
            showError(error);
        }
    }, [error]);

    const handleFilter = () => {
        let result = [...allDsps];

        if (filters.selectedDspIdsForName && filters.selectedDspIdsForName.length > 0) {
            const idSet = new Set(filters.selectedDspIdsForName);
            result = result.filter((dsp) => dsp.id != null && idSet.has(String(dsp.id)));
        }

        if (filters.dspCode) {
            result = result.filter(dsp => dsp.dspCode?.includes(filters.dspCode));
        }

        if (filters.warehouseId !== 'all') {
            result = result.filter(dsp => dsp.warehouseIds.includes(Number(filters.warehouseId)));
        }

        if (filters.statusFilter !== 'all') {
            result = result.filter(dsp => dsp.status === Number(filters.statusFilter));
        }

        setFilteredData(result);
        setCurrentPage(1);
    };

    const handleResetFilters = () => {
        setFilters(DEFAULT_DSP_FILTER_OPTIONS);
        setFilteredData(allDsps.filter(dsp => dsp.status === 1));
        setCurrentPage(1);
    };

    // Show new DSP drawer
    const showNewDspDrawer = () => {
        setSelectedDsp(null);
        setDrawerMode('create');
        setDrawerVisible(true);
    };

    // Show DSP details
    const showDspDetail = (dsp: DspType) => {
        setSelectedDsp(dsp);
        setDrawerMode('edit');
        setDrawerVisible(true);
    };

    // Handle DSP form submission (create or update)
    const handleDspSubmit = async (values: DspType) => {
        try {
            const dspInput = {
                id: values.id || undefined,
                name: values.name,
                fullName: values.fullName,
                dspCode: values.dspCode,
                contactName: values.contactName || undefined,
                contactPhone: values.contactPhone || undefined,
                contactEmail: values.contactEmail || undefined,
                operationEmail: values.operationEmail || undefined,
                status: values.status,
                paymentTermDays: values.paymentTermDays,
                exitDelayDays: values.exitDelayDays,
                warehouseIds: values.warehouseIds,
                openSettlementConfig: false
            };

            const {data} = await saveDspWithWarehouses({
                variables: {input: dspInput},
                refetchQueries: isHeadquarters ? [{query: GET_ALL_DSPS}] : [{query: GET_DSPS_BY_HUB_ID, variables: { hubId: hubId! }}]
            });

            if (drawerMode === 'create') {
                messageApi.success(t('central.dsp.messages.dspCreatedSuccessfully') as string);
            } else {
                messageApi.success(t('central.dsp.messages.dspUpdatedSuccessfully') as string);
            }

            setDrawerVisible(false);
            // 重新获取数据
            refetch();
            // 返回落库结果：新建时抽屉需要用 dspId 接着保存财务档案
            return data?.saveDspWithWarehouses;
        } catch (error: any) {
            showError(error);
            // 抛回抽屉，避免失败时仍被当作提交成功而自动关闭
            throw error;
        }
    };

    // Table column definitions
    const dspColumns: TableColumnsType<DspType> = [
        {
            title: t('central.dsp.columns.code'),
            dataIndex: 'dspCode',
            key: 'dspCode',
            width: 110,
            align: 'left',
            render: (text, record) => (
                <Button
                    type="link"
                    onClick={() => showDspDetail(record)}
                >
                    {text}
                </Button>
            ),
        },
        {
            title: t('central.dsp.columns.companyName'),
            dataIndex: 'name',
            key: 'name',
            width: 160,
            align: 'left',
        },
        {
            title: t('central.dsp.columns.fullName'),
            dataIndex: 'fullName',
            key: 'fullName',
            width: 180,
            align: 'left',
            ellipsis: {
                showTitle: false,
            },
            render: (text: string) => (
                <Tooltip placement="topLeft" title={text}>
                    {text}
                </Tooltip>
            ),
        },
        {
            title: t('central.dsp.columns.contactName'),
            dataIndex: 'contactName',
            key: 'contactName',
            width: 120,
            align: 'left',
        },
        {
            title: t('central.dsp.columns.phone'),
            dataIndex: 'contactPhone',
            key: 'contactPhone',
            width: 130,
            align: 'left',
        },
        {
            title: t('central.dsp.columns.warehouses'),
            dataIndex: 'id',
            key: 'warehouses',
            width: 180,
            align: 'left',
            render: (dspId: string, record: DspType) => {
                return record.warehouses.map(warehouse => warehouse.name).join(', ');
            }
        },
        {
            title: t('central.dsp.columns.status'),
            dataIndex: 'status',
            key: 'status',
            width: 100,
            align: 'left',
            render: (status: number) => (
                <Tag color={status === 1 ? 'green' : 'red'}>
                    {status === 1 ? t('enum.activeStatus.active') : t('enum.activeStatus.suspended')}
                </Tag>
            ),
        }
    ];

    if (loading) {
        return (
            <Loading spinning={loading}/>
        )
    }

    return (
            <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
            {/* Filter area */}
            <FilterArea
                warehousesOptions={warehouseOptions}
                loading={warehousesLoading}
                filters={filters}
                setFilters={setFilters}
                dspFilterHubId={isHeadquarters ? undefined : hubId}
            />

            {/* 批量操作区域 */}
            <div className="flex justify-end items-center mb-3 pl-0">
                <div className="flex items-center gap-2">
                    <Button
                        type="primary"
                        onClick={showNewDspDrawer}
                        loading={savingDsp}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('components.button.new')}</span>
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleFilter}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('components.button.filter')}</span>
                    </Button>
                    <Button
                        onClick={handleResetFilters}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('components.button.reset')}</span>
                    </Button>
                    </div>
            </div>

            {/* DSP table */}
            <div className="[&_.ant-table-body]:overflow-scroll [&_.ant-table-body]:overflow-x-scroll [&_.ant-table-body]:overflow-y-scroll">
                <Table
                    rowKey="id"
                    columns={dspColumns}
                    dataSource={filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize)}
                    loading={loading}
                    pagination={false}
                    scroll={{x: 1000}}
                    size="middle"
                    tableLayout="auto"
                 className="mb-4"/>
            </div>

            {/* 分页 */}
            <TablePaginationBar
                total={filteredData.length}
                current={currentPage}
                pageSize={pageSize}
                onChange={(page) => {
                    setCurrentPage(page);
                }}
                onShowSizeChange={(_, size) => {
                    setPageSize(size);
                    setCurrentPage(1);
                }}
                pageSizeOptions={['10', '20', '50', '100']}
            />

            {/* DSP form drawer (shared for create/edit) */}
            <DspFormDrawer
                warehousesOptions={warehouseOptions}
                visible={drawerVisible}
                onClose={() => setDrawerVisible(false)}
                onSubmit={handleDspSubmit}
                dsp={selectedDsp}
                mode={drawerMode}
            />
        </div>
    );
};

export default WmsDspPage;

